import { PROTOCOL_VERSION } from '../lib/constants';
import { saveContact, type StoredContact } from '../lib/storage';
import type { PeerSession } from '../webrtc/peer';
import {
  contactVerificationCode,
  createContactProof,
  createContactSeed,
  createLocalIdentity,
  deriveContactId,
  verifyContactProof,
} from './contact';

export interface IncomingContactProposal {
  proposalId: string;
  secret: string;
  peerIdentity: string;
}

interface ContactProtocolEvents {
  onProposal: (proposal: IncomingContactProposal) => void;
  onSaved: (contact: StoredContact) => void;
  onDeclined: () => void;
}

type ContactMessage =
  | { type: 'contact-propose'; protocolVersion: number; proposalId: string; secret: string; identity: string }
  | { type: 'contact-accept'; protocolVersion: number; proposalId: string; identity: string }
  | { type: 'contact-decline'; protocolVersion: number; proposalId: string }
  | { type: 'contact-auth'; protocolVersion: number; contactId: string; identity: string; proof: string };

interface PendingProposal {
  secret: string;
  localIdentity: string;
  name: string;
}

export class ContactProtocol {
  private peer: PeerSession;
  private role: 'host' | 'guest';
  private roomId: string;
  private events: ContactProtocolEvents;
  private pending = new Map<string, PendingProposal>();
  private unsubscribe: () => void;
  private authentication?: {
    contact: StoredContact;
    resolve: () => void;
    reject: (error: Error) => void;
    timeout: number;
    resend: number;
  };
  private pendingAuthenticationMessages = new Map<string, Extract<ContactMessage, { type: 'contact-auth' }>>();

  constructor(peer: PeerSession, roomId: string, role: 'host' | 'guest', events: ContactProtocolEvents) {
    this.peer = peer;
    this.roomId = roomId;
    this.role = role;
    this.events = events;
    this.unsubscribe = peer.onControl((value) => void this.handle(value).catch(() => undefined));
  }

  propose(name: string): void {
    const seed = createContactSeed();
    this.pending.set(seed.proposalId, { secret: seed.secret, localIdentity: seed.localIdentity, name });
    this.peer.sendControl({
      type: 'contact-propose',
      protocolVersion: PROTOCOL_VERSION,
      proposalId: seed.proposalId,
      secret: seed.secret,
      identity: seed.localIdentity,
    } satisfies ContactMessage);
  }

  async accept(proposal: IncomingContactProposal, name: string): Promise<StoredContact> {
    const now = Date.now();
    const contact: StoredContact = {
      id: await deriveContactId(proposal.secret),
      name: name.trim() || 'Trusted device',
      role: this.role,
      secret: proposal.secret,
      localIdentity: createLocalIdentity(),
      peerIdentity: proposal.peerIdentity,
      createdAt: now,
      updatedAt: now,
    };
    await saveContact(contact);
    this.peer.sendControl({
      type: 'contact-accept',
      protocolVersion: PROTOCOL_VERSION,
      proposalId: proposal.proposalId,
      identity: contact.localIdentity,
    } satisfies ContactMessage);
    this.events.onSaved(contact);
    return contact;
  }

  decline(proposal: IncomingContactProposal): void {
    this.peer.sendControl({ type: 'contact-decline', protocolVersion: PROTOCOL_VERSION, proposalId: proposal.proposalId } satisfies ContactMessage);
  }

  authenticate(contact: StoredContact): Promise<void> {
    if (this.authentication) return Promise.reject(new Error('Contact authentication is already running.'));
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        if (this.authentication) window.clearInterval(this.authentication.resend);
        this.authentication = undefined;
        reject(new Error('The saved contact did not prove its identity.'));
      }, 12_000);
      const sendProof = () => void createContactProof(contact, this.roomId).then((proof) => {
        this.peer.sendControl({
          type: 'contact-auth',
          protocolVersion: PROTOCOL_VERSION,
          contactId: contact.id,
          identity: contact.localIdentity,
          proof,
        } satisfies ContactMessage);
      }).catch(reject);
      const resend = window.setInterval(sendProof, 1_000);
      this.authentication = { contact, resolve, reject, timeout, resend };
      sendProof();
      const pending = this.pendingAuthenticationMessages.get(contact.id);
      if (pending) {
        this.pendingAuthenticationMessages.delete(contact.id);
        void this.handle(pending);
      }
    });
  }

  verificationCode(contact: StoredContact): Promise<string> {
    return contactVerificationCode(contact.secret);
  }

  close(): void {
    this.unsubscribe();
    if (this.authentication) {
      window.clearTimeout(this.authentication.timeout);
      window.clearInterval(this.authentication.resend);
      this.authentication.reject(new Error('Contact authentication was cancelled.'));
      this.authentication = undefined;
    }
  }

  private async handle(value: unknown): Promise<void> {
    if (!isContactMessage(value)) return;
    if (value.type === 'contact-propose') {
      this.events.onProposal({ proposalId: value.proposalId, secret: value.secret, peerIdentity: value.identity });
      return;
    }
    if (value.type === 'contact-accept') {
      const pending = this.pending.get(value.proposalId);
      if (!pending) return;
      this.pending.delete(value.proposalId);
      const now = Date.now();
      const contact: StoredContact = {
        id: await deriveContactId(pending.secret),
        name: pending.name.trim() || 'Trusted device',
        role: this.role,
        secret: pending.secret,
        localIdentity: pending.localIdentity,
        peerIdentity: value.identity,
        createdAt: now,
        updatedAt: now,
      };
      await saveContact(contact);
      this.events.onSaved(contact);
      return;
    }
    if (value.type === 'contact-decline') {
      if (this.pending.delete(value.proposalId)) this.events.onDeclined();
      return;
    }
    const authentication = this.authentication;
    if (!authentication) {
      if (this.pendingAuthenticationMessages.size >= 4 && !this.pendingAuthenticationMessages.has(value.contactId)) {
        const oldest = this.pendingAuthenticationMessages.keys().next().value;
        if (oldest) this.pendingAuthenticationMessages.delete(oldest);
      }
      this.pendingAuthenticationMessages.set(value.contactId, value);
      return;
    }
    if (value.contactId !== authentication.contact.id) return;
    const valid = await verifyContactProof(authentication.contact, this.roomId, value.identity, value.proof);
    window.clearTimeout(authentication.timeout);
    window.clearInterval(authentication.resend);
    this.authentication = undefined;
    if (valid) authentication.resolve();
    else authentication.reject(new Error('The connected device does not match this trusted contact.'));
  }
}

function isContactMessage(value: unknown): value is ContactMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<ContactMessage>;
  if (message.protocolVersion !== PROTOCOL_VERSION || typeof message.type !== 'string') return false;
  if (message.type === 'contact-propose') {
    return typeof message.proposalId === 'string' && message.proposalId.length <= 64
      && isKey(message.secret)
      && isKey(message.identity);
  }
  if (message.type === 'contact-accept') return typeof message.proposalId === 'string' && message.proposalId.length <= 64
    && isKey(message.identity);
  if (message.type === 'contact-decline') return typeof message.proposalId === 'string' && message.proposalId.length <= 64;
  return message.type === 'contact-auth'
    && typeof message.contactId === 'string' && message.contactId.length <= 64
    && isKey(message.identity)
    && isKey(message.proof);
}

function isKey(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{43}$/u.test(value);
}
